import {CheckCircleFilled, CloseCircleFilled, InfoCircleOutlined, PlusCircleOutlined} from "@ant-design/icons";
import {Progress, Tooltip} from "antd";
import mime from "mime";
import React from "react";
import { isEmptyString, isNullOrUndefined, isNullOrUndefinedOrEmpty } from "src/common/utils";
import {
  ErrorInUploadedFile,
  FileNotUploadedError,
  downloadFile,
} from "src/common/utils/file-utils";
import { sizeHumanReadableFormat } from "src/common/utils/size-human-readable-format";
import "./index.scss";

type Props = {
  fileCategoryName: string;
  allowedFileTypes: Array<string>;
  isMandatory: boolean;
  maximumSizeOfFileInBytes: number;
  description:string;
  handleUpload: (fileToBeUploaded: File, setProgress: (progress: number) => void) => Promise<File>;
};

function OptionalFileInfo(props: {
  isMandatory: boolean;
  uploadedFile: File | undefined;
  fileCategoryName: string;
}): React.ReactElement {
  return (
    <>
      {props.isMandatory===false && isNullOrUndefined(props.uploadedFile) ? (
        <>
          <div className="padding-container" />
          <span style={{ color: "red" }}>
            * If not uploaded, then the previously uploaded {props.fileCategoryName} will be reused
          </span>
        </>
      ) : null}
    </>
  );
}

function AlreadyUploaded(props: { uploadedFile: File}): React.ReactElement {
  return (
    <>
      <CheckCircleFilled
        style={{
          color: "#507600",
          fontSize: "24px",
        }}
      />
      <div className="upload-successfully-text">Uploaded Successfully</div>
      <div
        className="uploaded-file-name"
        onClick={() => {
          downloadFile(props.uploadedFile);
        }}
      >
        {props.uploadedFile.name}
      </div>
    </>
  );
}

function NewUpload(): React.ReactElement {
  return (
    <>
      <PlusCircleOutlined
        style={{
          fontSize: "24px",
          color: "#6A6A6A",
        }}
      />
      <div className="upload-text">Upload</div>
    </>
  );
}

function UploadMessage({
  error,
  displayAllowedFileTypesInString: displayAllowedFileTypes,
}: {
  error: String | null;
  displayAllowedFileTypesInString: String;
}) {
  return (
    <span>
      {isNullOrUndefinedOrEmpty(error) ? (
        <>
          {isEmptyString(displayAllowedFileTypes) ? (
            <>File of any type can be uploaded</>
          ) : (
            <>Allowed file type: {displayAllowedFileTypes}</>
          )}
        </>
      ) : (
        <>
          <CloseCircleFilled
            style={{
              fontSize: "14px",
              color: "#B02000",
            }}
          />{" "}
          <span className="error-text">{error}</span>
        </>
      )}
    </span>
  );
}

function UploadFile(props: Props) {
  const hiddenFileInput = React.useRef<HTMLInputElement>(null);
  const hiddenFormInput = React.useRef<HTMLFormElement>(null);
  const [uploadedFile, setUploadedFile] = React.useState<File | undefined>(undefined);
  const [isInputDisabled, setIsInputDisabled] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<String | null>(null);
  const [progress, setProgress] = React.useState<number>(0);

  const handleClick = (_event: React.MouseEvent<HTMLDivElement>) => {
    hiddenFileInput.current?.click();
  };

  function verifyUploadedFile(uploadedFile: File) {
    if (uploadedFile.size > props.maximumSizeOfFileInBytes) {
      throw new ErrorInUploadedFile(
        `Please upload a file with size less than ${sizeHumanReadableFormat(
          props.maximumSizeOfFileInBytes
        )}`
      );
    }
    if (
      !props.allowedFileTypes.some((fileType) => mime.getType(fileType) === uploadedFile.type) &&
      props.allowedFileTypes.length > 0
    ) {
      throw new ErrorInUploadedFile(
        `Invalid file type! Please upload a file of the given type(s) - ${displayAllowedFileTypesInString()}`
      );
    }
  }

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    setIsInputDisabled(true);
    try {
      const uploadedFile: File | undefined = event.target.files?.[0];
      if (uploadedFile === undefined) {
        throw new FileNotUploadedError("");
      }
      verifyUploadedFile(uploadedFile);
      const fileAfterUploading = await props.handleUpload(uploadedFile, setProgress);
      setUploadedFile(fileAfterUploading);
      setError(null);
    } catch (error) {
      if (error instanceof ErrorInUploadedFile) {
        setError(error.message);
      }
      if (hiddenFileInput.current?.value) {
        hiddenFileInput.current.value = "";
      }
      setIsInputDisabled(false);
    }
    setLoading(false);
  };

  function displayAllowedFileTypesInString(): string {
    return props.allowedFileTypes.map((fileType) => fileType.toUpperCase()).join(", ");
  }

  function allowedFileTypesInString(): string | undefined {
    return props.allowedFileTypes.length === 0
      ? undefined
      : props.allowedFileTypes.map((fileType) => mime.getType(fileType)).join(", ");
  }

  function requiredOrNot(): string {
    return props.isMandatory ? "Required" : "Optional";
  }

  return (
      <div className={`upload-container upload-container-${props.fileCategoryName}`}>
          <div className="heading-container">
              <div>
                  <span className="heading">{props.fileCategoryName}</span>
                  {" "}
                  <span className="mandatory-text">({requiredOrNot()})</span>
              </div>
              <div className="info-icon-container">
                  <Tooltip placement="bottom" title={props.description}>
                      <InfoCircleOutlined
                          style={{fontSize: "20px", stroke: "#5f9fab", strokeWidth: "30", cursor: "pointer"}}
                      />
                  </Tooltip>
              </div>
          </div>
              <div className="padding-container"/>
              <form ref={hiddenFormInput}>
                  <input
                      type="file"
                      ref={hiddenFileInput}
                      accept={allowedFileTypesInString()}
                      onChange={handleChange}
                      style={{display: "none"}}
                      disabled={isInputDisabled}
                  />
              </form>

              <div
                  className="upload-box"
                  onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                      handleClick(event);
                  }}
                  style={
                      isInputDisabled
                          ? {pointerEvents: "none", border: "1px solid #989898cc"}
                          : {border: "1px dashed #989898cc"}
                  }
              >
                  <div className="upload-box-elements">
                      {loading ? (
                          <Progress
                              type="circle"
                              percent={progress}
                              format={(progress) => {
                                  return (
                                      <div className="progress-container">
                                          <div>Uploading...</div>
                                          <div>{progress}%</div>
                                      </div>
                                  );
                              }}
                              status={"normal"}
                              strokeColor="#5f9fab"
                          />
                      ) : uploadedFile === undefined ? (
                          <NewUpload/>
                      ) : (
                          <AlreadyUploaded uploadedFile={uploadedFile}/>
                      )}
                  </div>
              </div>
          <div className="padding-container"/>
          <UploadMessage
              error={error}
              displayAllowedFileTypesInString={displayAllowedFileTypesInString()}
          />
         <OptionalFileInfo 
            uploadedFile={uploadedFile} 
            fileCategoryName={props.fileCategoryName} 
            isMandatory={props.isMandatory}
         />
      </div>

  );
}

export default UploadFile;
